// Shared Report (obj_type "R") viewer — criteria form → render → HTML
// preview / binary download. Used by the designer live preview, the in-app
// /run surface and the downloadable runtime client (one implementation,
// host-specific ReportRuntimeAdapter).
//
// Criteria behave EXACTLY like visual criteria: same VisualCriteriaForm
// (F2 lookup, item_change, defaults pre-seeded from resolvedParams), same
// needs-input contract. Inline-styled like CriteriaBar so the standalone
// runtime renders identically without the app CSS classes.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Download, Loader2, Play } from 'lucide-react'
import CriteriaBar from '../txn/CriteriaBar'
import { requiredIssues, type CriteriaIssue } from '../txn/criteriaFormat'
import type {
  ReportDefinition, ReportOutputFormat, ReportRuntimeAdapter,
} from './types'
import { tw } from '../testHooks'
import type { CustomizeTransport } from '../customize/model'
import { useCustomizeSession } from '../customize/session'
import { CustomizeBar, CustomizeMenu } from '../customize/ui'
import PromptCustomizer, { type PromptCard } from '../customize/PromptCustomizer'
import PromptPanel from '../customize/PromptPanel'

export interface ReportViewerProps {
  project: string
  objName: string
  adapter: ReportRuntimeAdapter
  /** Run immediately when every prompt has a value (default true). */
  autoRun?: boolean
  /** The ⋮ "Customize" entry for the organization's functional administrator:
   *  Customize mode on the criteria form (labels / order / hidden / read-only
   *  / defaults). Omitted or not enabled → no ⋮. */
  customize?: { transport: CustomizeTransport; enabled: boolean }
}

const FORMAT_LABEL: Record<ReportOutputFormat, string> = {
  html: 'HTML', pdf: 'PDF', excel: 'Excel', xlsx: 'Excel', csv: 'CSV',
  docx: 'Word', pptx: 'PowerPoint', rtf: 'RTF',
}

const actionBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13,
  padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--color-border)',
  background: 'var(--color-accent)', color: '#fff',
}

const ghostBtn: CSSProperties = {
  ...actionBtn, background: 'var(--color-bg)', color: 'var(--color-text)',
}

const selectStyle: CSSProperties = {
  fontSize: 13, padding: '3px 6px', borderRadius: 6,
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}

// Busy states. A real report routinely renders for a minute or more, and the
// spinner inside the Run button is too small to answer "did my click land?" —
// so the output area says so itself: it fills the empty area on a first run,
// and floats over the previous output on a re-run so that stays readable.
const busyBlock: CSSProperties = {
  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', gap: 10,
  background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
  fontSize: 14, textAlign: 'center', padding: 24,
}

const busyPill: CSSProperties = {
  position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
  zIndex: 2, display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '6px 14px', borderRadius: 999, fontSize: 13,
  border: '1px solid var(--color-border)', background: 'var(--color-bg)',
  color: 'var(--color-text)', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
}

const spin: CSSProperties = { animation: 'spin 1s linear infinite' }

/** Reassure once a render is long enough that the user starts to doubt it. */
const SLOW_AFTER_SECONDS = 15

function num(v: unknown, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) && String(v ?? '').trim() !== '' ? n : fallback
}

function toInputStr(v: unknown): string {
  if (v == null) return ''
  if (Array.isArray(v)) return v.map((x) => String(x)).join(',')
  return String(v)
}

export default function ReportViewer({
  project, objName, adapter, autoRun = true, customize,
}: ReportViewerProps) {
  // Bumped after Customize mode ends, to re-read the prompt form.
  const [defRevision, setDefRevision] = useState(0)
  const [customizing, setCustomizing] = useState(false)
  const [panel, setPanel] = useState<{ key: string; anchor: HTMLElement } | null>(null)
  const noTransport = useMemo<CustomizeTransport>(() => ({
    get: () => Promise.reject(new Error('Customize is not available here')),
    patch: () => Promise.reject(new Error('Customize is not available here')),
    reset: () => Promise.reject(new Error('Customize is not available here')),
  }), [])
  const session = useCustomizeSession({
    objType: 'R', objName, transport: customize?.transport ?? noTransport, active: customizing,
  })
  const [def, setDef] = useState<ReportDefinition | null>(null)
  const [defError, setDefError] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [format, setFormat] = useState<ReportOutputFormat>('html')
  const [collapsed, setCollapsed] = useState(false)
  const [running, setRunning] = useState(false)
  // Which format is in flight, and for how long — the busy state names the
  // work ("Preparing the PDF download…") instead of a bare spinner.
  const [runningFormat, setRunningFormat] = useState<ReportOutputFormat>('html')
  const [elapsed, setElapsed] = useState(0)
  const [runError, setRunError] = useState<string | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [missing, setMissing] = useState<string[]>([])
  // Findings of the criteria's checks: the server's reasons for refusing the
  // last run, or the bar's own required check.
  const [issues, setIssues] = useState<CriteriaIssue[]>([])
  // Monotonic run guard — a stale response must never clobber a newer one.
  const runSeq = useRef(0)
  const autoRanFor = useRef<string | null>(null)

  // Load the definition (prompt vars + resolved defaults + formats).
  useEffect(() => {
    let cancelled = false
    setDef(null)
    setDefError(null)
    setHtml(null)
    setValues({})
    autoRanFor.current = null
    adapter
      .fetchDefinition()
      .then((d) => {
        if (cancelled) return
        setDef(d)
        setFormat(d.defaultFormat || 'html')
        const seeded: Record<string, string> = {}
        for (const pv of d.promptVars) {
          seeded[pv.name] = toInputStr(d.resolvedParams[pv.name] ?? pv.default)
        }
        setValues(seeded)
        setCollapsed(d.promptVars.length === 0)
        setMissing(d.missingParams || [])
      })
      .catch((e: unknown) => {
        if (!cancelled) setDefError(e instanceof Error ? e.message : 'Failed to load report definition')
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, defRevision])

  const run = useCallback(
    async (fmt: ReportOutputFormat, vals: Record<string, string>) => {
      const seq = ++runSeq.current
      setRunning(true)
      setRunningFormat(fmt)
      setRunError(null)
      try {
        const outcome = await adapter.render({ ...vals }, fmt)
        if (seq !== runSeq.current) return
        if (outcome.needsInput) {
          setMissing(outcome.missingParams || [])
          setIssues(outcome.criteriaErrors || [])
          setCollapsed(false)
          setHtml(null)
          return
        }
        setMissing([])
        setIssues([])
        if (fmt === 'html') {
          setHtml(outcome.html || '')
          setCollapsed(true)
        } else if (outcome.blob) {
          // Binary output → download; keep the current HTML preview (if any).
          const url = URL.createObjectURL(outcome.blob)
          const a = document.createElement('a')
          a.href = url
          a.download = outcome.filename || `${objName}.${fmt === 'excel' || fmt === 'xlsx' ? 'xlsx' : fmt}`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
      } catch (e: unknown) {
        if (seq === runSeq.current) {
          setRunError(e instanceof Error ? e.message : 'Report rendering failed')
        }
      } finally {
        if (seq === runSeq.current) setRunning(false)
      }
    },
    [adapter, objName],
  )

  // A user's Run / Export: a blank required criterion is said at once rather
  // than asked of the server (which would refuse it the same way).
  const runChecked = (fmt: ReportOutputFormat) => {
    const local = def ? requiredIssues(def.promptVars, values) : []
    if (local.length) {
      setIssues(local)
      setCollapsed(false)
      return
    }
    void run(fmt, values)
  }

  // Count the wait up while a run is in flight — a still spinner and a
  // counting one read very differently at the 60-second mark.
  useEffect(() => {
    if (!running) return
    const startedAt = Date.now()
    setElapsed(0)
    const id = setInterval(
      () => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000,
    )
    return () => clearInterval(id)
  }, [running])

  // Auto-run once per object when every prompt already has a value.
  useEffect(() => {
    if (!autoRun || !def || autoRanFor.current === objName) return
    if (def.needsInput) return
    autoRanFor.current = objName
    void run('html', values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def, objName])

  const formats = useMemo<ReportOutputFormat[]>(() => {
    const list = (def?.formats || []).filter((f) => f in FORMAT_LABEL)
    if (!list.includes('html')) list.unshift('html')
    return [...new Set(list)]
  }, [def])

  if (defError) {
    return <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: 14 }}>{defError}</div>
  }
  if (!def) {
    return (
      <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)', fontSize: 14 }}>
        <Loader2 size={16} style={spin} /> Loading report…
      </div>
    )
  }

  const exportFormats = formats.filter((f) => f !== 'html')

  const busyLabel = runningFormat === 'html'
    ? 'Rendering the report…'
    : `Preparing the ${FORMAT_LABEL[runningFormat]} download…`

  const actions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {exportFormats.length > 0 && (
        <select
          value={format === 'html' ? (exportFormats[0] ?? 'pdf') : format}
          onChange={(e) => setFormat(e.target.value as ReportOutputFormat)}
          {...tw('field', '_export_format')}
          style={selectStyle}
          title="Export format"
        >
          {exportFormats.map((f) => (
            <option key={f} value={f}>{FORMAT_LABEL[f]}</option>
          ))}
        </select>
      )}
      {exportFormats.length > 0 && (
        <button
          style={ghostBtn}
          disabled={running}
          onClick={() => runChecked(format === 'html' ? (exportFormats[0] ?? 'pdf') : format)}
          title="Download the report in the selected format"
          {...tw('action', 'export')}
        >
          <Download size={13} /> Export
        </button>
      )}
      <button
        style={actionBtn}
        disabled={running}
        onClick={() => runChecked('html')}
        {...tw('action', 'apply')}
      >
        {running
          ? <Loader2 size={13} style={spin} />
          : <Play size={13} />} Run
      </button>
      {customize && !customizing && (
        <CustomizeMenu
          enabled={customize.enabled}
          subtitle="Arrange the criteria form for your organization"
          onCustomize={() => { setPanel(null); setCustomizing(true) }}
        />
      )}
    </div>
  )

  // ── Customize mode: the prompts as cards; Run / Export are off ─────────
  if (customizing) {
    const rules = ((session.effective as any)?.SQLMODEL?.CRITERIA?.query?.rules || []) as any[]
    const coreRules = ((session.core as any)?.SQLMODEL?.CRITERIA?.query?.rules || []) as any[]
    const prompts = rules
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => r && typeof r === 'object' && !Array.isArray(r.rules)
        && String(r.queryOption || '').trim().toUpperCase() === 'P' && (r.id || r.field))
      .sort((a, b) => (num(a.r.order, (a.i + 1) * 10) - num(b.r.order, (b.i + 1) * 10)) || (a.i - b.i))
      .map(({ r }) => r)
    const keyOf = (r: any) => String(r.id || r.field)
    const pathOf = (key: string, leaf: string) => `SQLMODEL.CRITERIA.query.rules.@items.${key}.${leaf}`
    const isMulti = (r: any) => ['in', 'not in', 'between'].includes(String(r.operator || '').toLowerCase())
    const valueText = (r: any) => isMulti(r) && Array.isArray(r.chipValue) && r.chipValue.length
      ? r.chipValue.join(', ') : String(r.value ?? '')
    const truthy = (v: unknown) => v === true || ['1', 'true', 'yes', 'y'].includes(String(v ?? '').toLowerCase())
    const cards: PromptCard[] = prompts.map((r) => ({
      key: keyOf(r),
      label: String(r.promptLabel || r.field || keyOf(r)),
      valueText: valueText(r),
      hidden: truthy(r.hidden),
      protect: truthy(r.protect),
      customized: session.countCustomized(`SQLMODEL.CRITERIA.query.rules.@items.${keyOf(r)}`) > 0,
      mandatory: !valueText(r),
    }))
    const open = panel ? prompts.find((r) => keyOf(r) === panel.key) : null
    const std = open ? coreRules.find((r) => keyOf(r) === keyOf(open)) : null
    const leaf = (key: string, l: string, value: unknown, label: string) =>
      void session.apply({ set: [{ path: pathOf(key, l), value }], reset: [] }, label)
    const reset = (key: string, l: string, label: string) =>
      void session.apply({ set: [], reset: [pathOf(key, l)] }, label)
    return (
      <div {...tw('screen', objName, { kind: 'R', state: 'customize' })}
        style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--color-bg)' }}>
        <CustomizeBar
          title={def.title || objName}
          session={session}
          onDone={() => { setCustomizing(false); setPanel(null); setDefRevision((n) => n + 1) }}
          resetOptions={[{ label: 'Reset the criteria form', count: session.countCustomized(), run: () => { void session.resetAll() } }]}
        />
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border)' }}>
          {prompts.length === 0
            ? <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>This report has no criteria prompts to arrange.</div>
            : <PromptCustomizer
                items={cards}
                openKey={panel?.key}
                onOpen={(key, anchor) => setPanel({ key, anchor })}
                onShow={(key) => leaf(key, 'hidden', false, 'Show prompt')}
                onReorder={(keys) => void session.apply({
                  set: keys.map((k, i) => ({ path: pathOf(k, 'order'), value: (i + 1) * 10 })), reset: [],
                }, 'Reorder prompts')} />}
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: 'var(--color-text-secondary)' }}>
          The report output stays here. Run is off while customizing.
        </div>
        {open && panel && (
          <PromptPanel
            anchor={panel.anchor}
            onClose={() => setPanel(null)}
            title={String(open.promptLabel || open.field)}
            sub={open.JASPER_PARAM ? `$P{${open.JASPER_PARAM}}` : String(open.field || '')}
            saved={session.status === 'saved'}
            needsDefaultToFix
            label={{
              value: String(open.promptLabel ?? ''), standard: String(std?.promptLabel ?? ''),
              customized: session.isCustomized(pathOf(keyOf(open), 'promptLabel')),
              onReset: () => reset(keyOf(open), 'promptLabel', 'Label'),
              onCommit: (v) => leaf(keyOf(open), 'promptLabel', v.trim() || String(std?.promptLabel ?? ''), 'Label'),
            }}
            show={{ on: !truthy(open.hidden), onChange: (on) => leaf(keyOf(open), 'hidden', !on, on ? 'Show prompt' : 'Hide prompt'), hint: 'Hidden prompts run on their default' }}
            editable={{ on: !truthy(open.protect), onChange: (on) => leaf(keyOf(open), 'protect', !on, on ? 'Editable' : 'Read-only'), hint: 'Off fixes it for your organization' }}
            defaultValue={{
              value: valueText(open),
              customized: session.isCustomized(pathOf(keyOf(open), isMulti(open) ? 'chipValue' : 'value')),
              placeholder: 'Leave empty to make people choose',
              hint: isMulti(open) ? 'Several values: separate them with commas.' : undefined,
              onReset: () => reset(keyOf(open), isMulti(open) ? 'chipValue' : 'value', 'Default'),
              onCommit: (v) => isMulti(open)
                ? leaf(keyOf(open), 'chipValue', v.split(',').map((x) => x.trim()).filter(Boolean), 'Default')
                : leaf(keyOf(open), 'value', v, 'Default'),
            }}
            onResetAll={() => void session.resetUnder([`SQLMODEL.CRITERIA.query.rules.@items.${keyOf(open)}`], 'Reset prompt')}
            resetAllDisabled={!session.countCustomized(`SQLMODEL.CRITERIA.query.rules.@items.${keyOf(open)}`)}
          />
        )}
      </div>
    )
  }

  return (
    <div {...tw('screen', objName, {
      kind: 'R',
      state: running ? 'loading' : runError ? 'error' : missing.length ? 'needs_input'
        : html != null ? 'ready' : 'idle',
    })}
         style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--color-bg)' }}>
      <CriteriaBar
        project={project}
        promptVars={def.promptVars}
        values={values}
        onChange={(name, val) => setValues((prev) => ({ ...prev, [name]: val }))}
        onSubmit={() => runChecked('html')}
        runtime={adapter.criteria}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        actions={actions}
        issues={issues}
      />
      {missing.length > 0 && issues.length === 0 && (
        <div style={{ padding: '6px 12px', fontSize: 13, color: 'var(--color-warning, #b58900)', flexShrink: 0 }}>
          Enter values for: {missing.join(', ')}
        </div>
      )}
      {runError && (
        <div {...tw('msg', 'error')} style={{ padding: '6px 12px', fontSize: 13, color: 'var(--color-error, #d33)', flexShrink: 0 }}>
          {runError}
        </div>
      )}
      <div style={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
        {html != null ? (
          <iframe
            title={def.title || objName}
            sandbox=""
            srcDoc={html}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        ) : !running && (
          <div style={{ padding: 24, fontSize: 14, color: 'var(--color-text-secondary)' }}>
            {def.needsInput || missing.length > 0
              ? 'Fill the criteria above, then press Run.'
              : 'Press Run to generate the report.'}
          </div>
        )}
        {running && (html != null ? (
          <div style={busyPill}>
            <Loader2 size={13} style={spin} />
            {busyLabel}{elapsed > 0 && ` · ${elapsed}s`}
          </div>
        ) : (
          <div style={busyBlock}>
            <Loader2 size={22} style={spin} />
            <div style={{ color: 'var(--color-text)', fontWeight: 500 }}>{busyLabel}</div>
            <div style={{ fontSize: 13 }}>
              {elapsed > 0 && `${elapsed}s`}
              {elapsed >= SLOW_AFTER_SECONDS
                && ' — big reports can take a minute or two. You can leave this open.'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
